import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { setDocData } from "../../../utils/firebase/Firebase.utils";
import toast from "react-hot-toast";
import {
  storeNotification,
  createPaymentStatusNotification,
} from "../../../services/notificationService";
import { queryAllDocsByFieldSnap } from "../../../utils/firebase/Firebase.utils";
import {
  FiDollarSign,
  FiClipboard,
  FiCalendar,
  FiCheck,
  FiX,
  FiImage,
  FiFileText,
  FiUser,
  FiMessageSquare,
  FiMaximize2,
  FiZoomIn,
  FiRotateCcw,
  FiAlertTriangle,
} from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import { sendNotification } from "../../../utils/notifications";
import { Subject } from "@mui/icons-material";
import { addNewNotification } from "../../../utils/EleghoUtils";

// Portal component to render content at document.body level
const Portal = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  return mounted ? ReactDOM.createPortal(children, document.body) : null;
};

export default function BillingPageDetails({ data, setViewMode }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [adminRemarks, setAdminRemarks] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [showImageModal, setShowImageModal] = useState(false);
  const [showRevertOptions, setShowRevertOptions] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Find the user email based on the identifier
  useEffect(() => {
    const fetchUserEmail = async () => {
      if (data?.identifier) {
        await queryAllDocsByFieldSnap(
          "users",
          "linkedIdentifiers",
          "array-contains",
          data.identifier,
          (users) => {
            if (users.length > 0) {
              setUserEmail(users[0].email);
            }
          }
        );
      }
    };
    fetchUserEmail();
  }, [data]);

  // Disable body scrolling when image modal is open
  useEffect(() => {
    if (showImageModal) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = "hidden";

      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [showImageModal]);

  const handleVerify = async (isApproved) => {
    if (!userEmail) {
      toast.error("Could not find user email to send notification");
      return;
    }

    setIsProcessing(true);

    try {
      const status = isApproved ? "paid" : "rejected";

      // Update billing status in Firestore
      await setDocData("billings", data.id, {
        status,
        verifiedDate: new Date().toLocaleDateString(),
        adminRemarks:
          adminRemarks ||
          (isApproved ? "Payment verified" : "Payment rejected"),
      });

      // Create notification for the user
      const fcmNotification = {
        title: "Payment Verification",
        body: `Your payment has been ${isApproved ? "verified" : "rejected"}.`,
        data: {
          type: "payment_verification",
          billingId: data.id,
        },
      };

      await sendNotification(data.userEmail, fcmNotification);

      const firestoreNotification = {
        to: data.userEmail,
        user: data.userEmail,
        subject: "Payment Verification",
        message: `Your payment has been ${
          isApproved ? "verified" : "rejected"
        }.`,
        type: "payment_verification",
        billingId: data.id,
        read: false,
        dateTime: new Date(),
      };

      await addNewNotification(firestoreNotification);

      toast.success(
        `Payment ${isApproved ? "verified" : "rejected"} successfully`
      );
      setViewMode(false);
    } catch (error) {
      console.error("Error verifying payment:", error);
      toast.error("Error processing payment verification");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRevertPayment = async (newStatus) => {
    if (!userEmail) {
      toast.error("Could not find user email to send notification");
      return;
    }

    setIsProcessing(true);

    try {
      // Update billing status in Firestore
      await setDocData("billings", data.id, {
        status: newStatus,
        revertDate: new Date().toLocaleDateString(),
        adminRemarks: adminRemarks || `Payment status reverted to ${newStatus}`,
      });

      // Create notification for the user
      const fcmNotification = {
        title: "Payment Verification",
        body: `Your payment has been reverted to ${newStatus}.`,
        data: {
          type: "payment_reversion",
          billingId: data.id,
        },
      };

      await sendNotification(data.userEmail, fcmNotification);

      const firestoreNotification = {
        to: data.userEmail,
        user: data.userEmail,
        subject: "Payment Verification",
        message: `Your payment has been reverted to ${newStatus}.`,
        type: "payment_reversion",
        billingId: data.id,
        read: false,
        dateTime: new Date(),
      };

      await addNewNotification(firestoreNotification);

      toast.success(`Payment status successfully reverted to ${newStatus}`);
      setViewMode(false);
    } catch (error) {
      console.error("Error reverting payment status:", error);
      toast.error("Error processing payment status reversion");
    } finally {
      setIsProcessing(false);
      setShowRevertOptions(false);
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <div className="page relative text-gray-600 text-sm text-left p-6 max-w-4xl mx-auto">
      <button
        className="absolute top-4 right-4 bg-gray-200 p-2 rounded-full hover:bg-gray-300 transition-colors"
        onClick={() => setViewMode(false)}
      >
        <FiX className="w-5 h-5" />
      </button>

      <div className="header text-xl font-bold text-gray-800 mb-6">
        Billing Details
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <FiClipboard className="w-5 h-5 text-eleghoBlue" />
            Billing Information
          </h3>

          <div className="space-y-4">
            <div className="flex flex-col">
              <span className="text-gray-500 text-xs">Identifier</span>
              <span className="font-medium">{data?.identifier}</span>
            </div>

            <div className="flex flex-col">
              <span className="text-gray-500 text-xs">Resident Name</span>
              <span className="font-medium">{data?.name}</span>
            </div>

            <div className="flex flex-col">
              <span className="text-gray-500 text-xs">Billing Type</span>
              <span className="font-medium">{data?.billingType}</span>
            </div>

            <div className="flex flex-col">
              <span className="text-gray-500 text-xs">Amount</span>
              <span className="font-medium text-eleghoGreen">
                ₱{data?.amount}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <FiCalendar className="w-5 h-5 text-eleghoBlue" />
            Schedule Information
          </h3>

          <div className="space-y-4">
            <div className="flex flex-col">
              <span className="text-gray-500 text-xs">Billing Date</span>
              <span className="font-medium">{data?.billingDate}</span>
            </div>

            <div className="flex flex-col">
              <span className="text-gray-500 text-xs">Due Date</span>
              <span className="font-medium">{data?.dueDate}</span>
            </div>

            <div className="flex flex-col">
              <span className="text-gray-500 text-xs">Status</span>
              <span
                className={`font-medium capitalize ${
                  data?.status === "paid"
                    ? "text-green-600"
                    : data?.status === "overdue"
                    ? "text-red-600"
                    : data?.status === "deposit slip uploaded"
                    ? "text-purple-600"
                    : "text-eleghoBlue"
                }`}
              >
                {data?.status}
              </span>
            </div>

            {data?.paymentDate && (
              <div className="flex flex-col">
                <span className="text-gray-500 text-xs">Payment Date</span>
                <span className="font-medium">{data?.paymentDate}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <FiFileText className="w-5 h-5 text-eleghoBlue" />
          Description
        </h3>
        <p className="text-gray-700">{data?.description}</p>
      </div>

      {/* Always show deposit slip section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <FiImage className="w-5 h-5 text-eleghoBlue" />
          Deposit Slip
        </h3>

        {data?.depositSlip && data.depositSlip.trim() !== "" ? (
          <div className="flex flex-col items-center">
            {!imageError ? (
              <>
                <div
                  className="relative group cursor-pointer"
                  onClick={() => !imageError && setShowImageModal(true)}
                >
                  <img
                    src={data.depositSlip}
                    alt="Deposit Slip"
                    className="max-w-full h-auto max-h-96 rounded-lg shadow-md border border-gray-200 transition-all duration-200 group-hover:brightness-90"
                    onError={handleImageError}
                  />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="bg-black bg-opacity-50 rounded-full p-3">
                      <FiZoomIn className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowImageModal(true)}
                  className="mt-4 text-eleghoBlue hover:underline flex items-center gap-1"
                >
                  <FiMaximize2 className="w-4 h-4" />
                  View Full Image
                </button>
              </>
            ) : (
              <div className="text-center py-4 px-6 bg-red-50 border border-red-200 rounded-lg">
                <FiAlertTriangle className="w-6 h-6 text-red-500 mx-auto mb-2" />
                <p className="text-red-500">No Image Uploaded</p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-gray-500 italic">No deposit slip uploaded</p>
          </div>
        )}
      </div>

      {data?.remarks && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <FiMessageSquare className="w-5 h-5 text-eleghoBlue" />
            User Remarks
          </h3>
          <p className="text-gray-700">{data?.remarks}</p>
        </div>
      )}

      {data?.status === "deposit slip uploaded" && (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <FiMessageSquare className="w-5 h-5 text-eleghoBlue" />
              Admin Remarks
            </h3>
            <textarea
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-eleghoBlue"
              rows="3"
              placeholder="Enter remarks for the resident (optional)"
              value={adminRemarks}
              onChange={(e) => setAdminRemarks(e.target.value)}
            ></textarea>
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => handleVerify(true)}
              disabled={isProcessing}
              className="flex-1 bg-eleghoGreen text-white py-3 px-4 rounded-lg font-medium hover:bg-opacity-90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                <>
                  <FiCheck className="w-5 h-5" />
                  Verify Payment
                </>
              )}
            </button>

            <button
              onClick={() => handleVerify(false)}
              disabled={isProcessing}
              className="flex-1 bg-red-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-opacity-90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                <>
                  <FiX className="w-5 h-5" />
                  Reject Payment
                </>
              )}
            </button>
          </div>
        </>
      )}

      {data?.status === "paid" && (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <FiMessageSquare className="w-5 h-5 text-eleghoBlue" />
              Admin Remarks
            </h3>
            <textarea
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-eleghoBlue"
              rows="3"
              placeholder="Enter remarks for reverting payment status (optional)"
              value={adminRemarks}
              onChange={(e) => setAdminRemarks(e.target.value)}
            ></textarea>
          </div>

          <div className="flex flex-col gap-4">
            {!showRevertOptions ? (
              <button
                onClick={() => setShowRevertOptions(true)}
                disabled={isProcessing}
                className="bg-yellow-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-opacity-90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <FiRotateCcw className="w-5 h-5" />
                Revert Payment Status
              </button>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                <h3 className="font-semibold text-gray-700 mb-4">
                  Select status to revert to:
                </h3>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => handleRevertPayment("deposit slip uploaded")}
                    disabled={isProcessing}
                    className="flex-1 bg-purple-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-opacity-90 transition-colors"
                  >
                    {isProcessing ? "Processing..." : "Deposit Slip Uploaded"}
                  </button>
                  <button
                    onClick={() => handleRevertPayment("unpaid")}
                    disabled={isProcessing}
                    className="flex-1 bg-blue-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-opacity-90 transition-colors"
                  >
                    {isProcessing ? "Processing..." : "Unpaid"}
                  </button>
                  <button
                    onClick={() => setShowRevertOptions(false)}
                    disabled={isProcessing}
                    className="flex-1 bg-gray-400 text-white py-3 px-4 rounded-lg font-medium hover:bg-opacity-90 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Image Viewer Modal using Portal - Only show when deposit slip exists and image hasn't errored */}
      {showImageModal &&
        data?.depositSlip &&
        data.depositSlip.trim() !== "" &&
        !imageError && (
          <Portal>
            <div
              className="fixed inset-0 z-[9999] flex items-center justify-center"
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              }}
              onClick={() => setShowImageModal(false)}
            >
              <div className="absolute inset-0 bg-black bg-opacity-90"></div>
              <div
                className="relative z-[10000] max-w-5xl w-full mx-4"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="absolute top-[-50px] right-0 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-colors"
                  onClick={() => setShowImageModal(false)}
                >
                  <FiX className="w-6 h-6" />
                </button>
                <img
                  src={data.depositSlip}
                  alt="Deposit Slip"
                  className="w-full h-auto max-h-[85vh] object-contain rounded-lg shadow-xl"
                  onClick={(e) => e.stopPropagation()}
                  onError={() => {
                    setImageError(true);
                    setShowImageModal(false);
                    toast.error("Failed to load deposit slip image");
                  }}
                />
              </div>
            </div>
          </Portal>
        )}
    </div>
  );
}
